import { Alert, Button, Group, Loader, Stack, Text } from "@mantine/core";
import { IconFiles, IconRefresh } from "@tabler/icons-react";
import React from "react";
import {
  GetProjectByProjectIdIssuesFilesOnBoardApiResponse,
  useGetProjectByProjectIdIssuesFilesOnBoardQuery,
} from "../../../../../openapi";
import { selectCurrentProjectId } from "../../../../global/globalSliceCommon";
import { useAppSelector } from "../../../../hooks";
import { selectIssueBoardId } from "../redux/selectors/issueCommonSelectors";
import SettingsCardWrapper from "./SettingsCardWrapper";

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** unitIndex;

  return `${value.toLocaleString("nb-NO", { maximumFractionDigits: value >= 10 ? 0 : 1 })} ${units[unitIndex]}`;
};

const getStats = (filesByIssueId?: GetProjectByProjectIdIssuesFilesOnBoardApiResponse) => {
  const entries = Object.entries(filesByIssueId ?? {});
  const files = entries.flatMap(([, issueFiles]) => issueFiles);
  const totalFiles = files.length;
  const issuesWithFiles = entries.length;
  const totalSize = files.reduce((sum, file) => sum + (file.size ?? 0), 0);
  const averageFilesPerIssue = issuesWithFiles === 0 ? 0 : totalFiles / issuesWithFiles;

  return {
    averageFilesPerIssue,
    issuesWithFiles,
    totalFiles,
    totalSize,
  };
};

const StatValue: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <Stack gap={0} miw={150}>
    <Text size="sm" c="dimmed">
      {label}
    </Text>
    <Text size="xl" fw={600}>
      {value}
    </Text>
  </Stack>
);

const IssueBoardFilesStats: React.FC = () => {
  const projectId = useAppSelector(selectCurrentProjectId);
  const issueboardId = useAppSelector(selectIssueBoardId);
  const [shouldFetchFiles, setShouldFetchFiles] = React.useState(false);
  const { data, isFetching, isError, refetch } = useGetProjectByProjectIdIssuesFilesOnBoardQuery(
    { projectId: projectId ?? "", issueboardId: issueboardId ?? "" },
    { skip: shouldFetchFiles === false || projectId == null || issueboardId == null },
  );
  const hasFetched = shouldFetchFiles && data != null;
  const stats = getStats(data);

  const fetchFiles = () => {
    if (projectId == null || issueboardId == null) {
      return;
    }

    if (shouldFetchFiles) {
      refetch();
      return;
    }

    setShouldFetchFiles(true);
  };

  return (
    <SettingsCardWrapper title="Filer">
      <Stack gap="md">
        {isError && (
          <Alert color="red" variant="light">
            Kunne ikke hente filer for registeret.
          </Alert>
        )}

        <Group>
          <Button
            variant="light"
            onClick={fetchFiles}
            disabled={isFetching}
            leftSection={
              isFetching ? <Loader size={16} /> : hasFetched ? <IconRefresh size={16} /> : <IconFiles size={16} />
            }
          >
            {hasFetched ? "Oppdater filer" : "Hent filer"}
          </Button>
        </Group>

        {hasFetched && (
          <Group gap="xl" align="flex-start">
            <StatValue label="Filer" value={stats.totalFiles.toLocaleString("nb-NO")} />
            <StatValue label="Saker med filer" value={stats.issuesWithFiles.toLocaleString("nb-NO")} />
            <StatValue label="Total størrelse" value={formatFileSize(stats.totalSize)} />
            <StatValue
              label="Snitt per sak"
              value={stats.averageFilesPerIssue.toLocaleString("nb-NO", { maximumFractionDigits: 1 })}
            />
          </Group>
        )}
      </Stack>
    </SettingsCardWrapper>
  );
};

export default IssueBoardFilesStats;
